// alarm-management.service.ts (Angular)
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { AlarmManagementModel } from '@models/alarm-management.model';
import { api } from '@consts/url.const';

@Injectable({
  providedIn: 'root',
})
export class AlarmManagementService {
  constructor(private http: HttpClient) {}

  checkActiveAlarm(locationId: string): Observable<AlarmManagementModel.AlarmCheckResponse> {
    return this.http.get<AlarmManagementModel.AlarmCheckResponse>(`${api.alarmManagement}/check/${locationId}`);
  }

  createAlarm(createAlarm: AlarmManagementModel.CreateAlarm): Observable<{ alarmId: string }> {
    return this.http.post<{ alarmId: string }>(`${api.alarmManagement}`, createAlarm);
  }

  cancelAlarm(cancelAlarm: AlarmManagementModel.CancelAlarm): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${api.alarmManagement}/cancel`, cancelAlarm);
  }

  cancelAlarmById(alarmId: string): Observable<{ message: string }> {
    return this.http.delete<{ message: string }>(`${api.alarmManagement}/${alarmId}` );
  }

  getActiveAlarms(): Observable<AlarmManagementModel.AlarmData[]> {
    return this.http.get<AlarmManagementModel.AlarmData[]>(`${api.alarmManagement}/active`);
  }

  getCanceledAlarms(): Observable<AlarmManagementModel.AlarmData[]> {
    return this.http.get<AlarmManagementModel.AlarmData[]>(`${api.alarmManagement}/canceled`);
  }

  getAlarmHistory(filter?: AlarmManagementModel.AlarmFilter): Observable<AlarmManagementModel.AlarmData[]> {
    const params: any = {};
    if (filter?.status !== undefined) params.status = filter.status;
    if (filter?.locationId) params.locationId = filter.locationId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;

    return this.http.get<AlarmManagementModel.AlarmData[]>(`${api.alarmManagement}/history`, { params });
  }

  getAlarmById(alarmId: string): Observable<AlarmManagementModel.AlarmData> {
    return this.http.get<AlarmManagementModel.AlarmData>(`${api.alarmManagement}/${alarmId}`);
  }

  // New methods for alert instances and Noonlight activity

  getAlarmsWithAlerts(
    filter?: AlarmManagementModel.AlarmsWithAlertsFilter
  ): Observable<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.AlarmWithAlerts>> {
    const params: any = {};
    if (filter?.status !== undefined) params.status = filter.status;
    if (filter?.locationId) params.locationId = filter.locationId;
    if (filter?.limit !== undefined) params.limit = filter.limit;
    if (filter?.offset !== undefined) params.offset = filter.offset;

    return this.http.get<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.AlarmWithAlerts>>(
      `${api.alarmManagement}/with-alerts`,
      { params }
    );
  }

  getAlarmAlertInstances(
    alarmId: string,
    pagination?: AlarmManagementModel.PaginationParams
  ): Observable<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.AlertInstanceSummary>> {
    const params: any = {};
    if (pagination?.limit !== undefined) params.limit = pagination.limit;
    if (pagination?.offset !== undefined) params.offset = pagination.offset;

    return this.http.get<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.AlertInstanceSummary>>(
      `${api.alarmManagement}/${alarmId}/alerts`,
      { params }
    );
  }

  getAlertNoonlightActivity(alertInstanceId: string | number): Observable<AlarmManagementModel.NoonlightActivityLog[]> {
    return this.http.get<AlarmManagementModel.NoonlightActivityLog[]>(
      `${api.alarmManagement}/alerts/${alertInstanceId}/noonlight-activity`
    );
  }

  getAlertActivityLogs(alertInstanceId: string | number): Observable<AlarmManagementModel.AlertActivityLog[]> {
    return this.http.get<AlarmManagementModel.AlertActivityLog[]>(
      `${api.alarmManagement}/alerts/${alertInstanceId}/activity`
    );
  }

  getRejectedAlerts(
    filter?: AlarmManagementModel.RejectedAlertsFilter & AlarmManagementModel.PaginationParams
  ): Observable<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.RejectedAlert>> {
    const params: any = {};
    if (filter?.locationId) params.locationId = filter.locationId;
    if (filter?.fromDate) params.fromDate = filter.fromDate;
    if (filter?.toDate) params.toDate = filter.toDate;
    if (filter?.limit !== undefined) params.limit = filter.limit;
    if (filter?.offset !== undefined) params.offset = filter.offset;

    return this.http.get<AlarmManagementModel.PaginatedResponse<AlarmManagementModel.RejectedAlert>>(
      `${api.alarmManagement}/rejected-alerts`,
      { params }
    );
  }
}
